import React, { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, FileDown, Maximize2 } from "lucide-react";
import { insightResources } from "@/lib/content";
import { usePublishedContent } from "@/lib/content/published";

/**
 * In-app PDF preview at /insights/resource/:slug
 *
 * PWAs on iOS hand `target="_blank"` PDFs to a system-level full-screen
 * viewer with no navigation chrome — users get stuck. This page solves it:
 * the PDF is embedded inside our shell with a clear back button, a
 * download button and a "fullscreen / open in browser" option for users
 * who want the native viewer experience.
 */
export default function ResourcePreview() {
  const { slug } = useParams();
  const { resources: blendedResources } = usePublishedContent();
  const resource = useMemo(
    () =>
      blendedResources.find((r) => r.slug === slug) ||
      insightResources.find((r) => r.slug === slug),
    [slug, blendedResources]
  );

  if (!resource) return <Navigate to="/insights" replace />;

  const filename = `hidroverse-${resource.slug}.pdf`;
  const pdfUrl = resource.pdf;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "#0A1A28", color: "#F5F7FA" }}
      data-testid={`resource-preview-${resource.slug}`}
    >
      <Helmet>
        <title>{`HidroVerse PDF — ${resource.title}`}</title>
        <meta name="description" content={resource.summary || resource.description || `Download "${resource.title}" — a HidroVerse engineering reference for commercial water systems.`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${resource.title} — HidroVerse engineering reference`} />
        <meta property="og:description" content={resource.summary || resource.description || ""} />
      </Helmet>

      {/* HEADER BAR — back + download + fullscreen */}
      <div
        className="sticky top-0 z-30 px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap"
        style={{
          background: "rgba(7, 18, 28, 0.92)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(31,182,193,0.18)",
        }}
      >
        <Link
          to="/insights"
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 font-semibold"
          style={{
            background: "transparent",
            color: "#F5F7FA",
            border: "1px solid rgba(245,247,250,0.30)",
            fontSize: "0.78rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            borderRadius: 4,
          }}
          data-testid="resource-preview-back"
        >
          <ArrowLeft size={14} aria-hidden />
          <span className="hidden xs:inline sm:inline">Back to Insights</span>
          <span className="xs:hidden sm:hidden">Back</span>
        </Link>

        <div className="flex-1 min-w-0 hidden md:block">
          <div
            className="truncate text-[0.92rem] font-semibold"
            style={{ color: "#F5F7FA" }}
          >
            {resource.title}
          </div>
          <div
            className="truncate text-[0.72rem]"
            style={{ color: "rgba(245,247,250,0.55)" }}
          >
            {resource.pages} pp · PDF · {resource.category}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 font-semibold"
            style={{
              background: "transparent",
              color: "#F5F7FA",
              border: "1px solid rgba(245,247,250,0.30)",
              fontSize: "0.74rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              borderRadius: 4,
            }}
            data-testid="resource-preview-fullscreen"
            aria-label="Open in browser fullscreen"
          >
            <Maximize2 size={13} aria-hidden />
            <span className="hidden sm:inline">Fullscreen</span>
          </a>
          <a
            href={pdfUrl}
            download={filename}
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 font-semibold"
            style={{
              background: "#1FB6C1",
              color: "#070A0D",
              fontSize: "0.78rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              borderRadius: 4,
            }}
            data-testid="resource-preview-download"
          >
            <FileDown size={14} aria-hidden />
            Download
          </a>
        </div>
      </div>

      {/* MOBILE TITLE BAND — full title when header is too tight */}
      <div
        className="md:hidden px-4 py-3"
        style={{
          background: "#0C2A40",
          borderBottom: "1px solid rgba(31,182,193,0.18)",
        }}
      >
        <div className="text-[0.95rem] font-semibold leading-snug">
          {resource.title}
        </div>
        <div
          className="text-[0.7rem] mt-1"
          style={{ color: "rgba(245,247,250,0.55)" }}
        >
          {resource.pages} pp · PDF
        </div>
      </div>

      {/* PDF VIEWPORT — iframe on tablet/desktop, object fallback on mobile */}
      <div
        className="flex-1 relative"
        style={{ background: "#0A1A28", minHeight: "70vh" }}
      >
        <object
          data={`${pdfUrl}#view=FitH&toolbar=1`}
          type="application/pdf"
          className="w-full h-full"
          style={{ minHeight: "calc(100vh - 110px)", border: "0" }}
          aria-label={`${resource.title} (PDF)`}
        >
          {/* Fallback for browsers that cannot embed a PDF (iOS Safari etc.) */}
          <div
            className="flex flex-col items-center justify-center text-center px-6 py-16 gap-4"
            style={{ minHeight: "60vh" }}
          >
            <div
              className="text-[1.05rem] font-semibold"
              style={{ color: "#F5F7FA" }}
            >
              Your browser cannot preview this PDF inline.
            </div>
            <div
              className="text-[0.88rem]"
              style={{ color: "rgba(245,247,250,0.65)", maxWidth: 460 }}
            >
              Tap <strong>Download</strong> to save the file to your device, or
              tap <strong>Fullscreen</strong> to open it in your browser's PDF
              viewer.
            </div>
            <div className="flex gap-2 mt-2">
              <a
                href={pdfUrl}
                download={filename}
                className="inline-flex items-center gap-2 px-5 py-2.5 font-semibold"
                style={{
                  background: "#1FB6C1",
                  color: "#070A0D",
                  fontSize: "0.78rem",
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  borderRadius: 4,
                }}
                data-testid="resource-preview-download-fallback"
              >
                <FileDown size={14} aria-hidden /> Download PDF
              </a>
            </div>
          </div>
        </object>
      </div>
    </div>
  );
}
